import * as TogglePrimitive from '@radix-ui/react-toggle';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/helpers/ui-utils';

const toggleVariants = cva(
  [
    'outline-hidden',
    'bg-foreground/20',
    'inline-flex',
    'items-center',
    'justify-center',
    'rounded-full',
    'border-none',
    'transition-all',
    'duration-200',
    'p-0',
    'w-10',
    'h-6',
    'hover:cursor-pointer',
    'focus-visible:outline-hidden',
    'after:transition-all',
    'after:ease',
    'after:duration-300',
    'after:content-[""]',
    'after:rounded-full',
    'after:bg-white',
    'after:h-5',
    'after:w-5',
    'after:translate-x-[-8px]',
    'after:data-[state=on]:translate-x-2',
    'hover:after:ring-[6px]',
    'hover:after:ring-white/30',
    'focus:ring',
    'focus:ring-[4px]',
    'focus:ring-[#E6ECFF]',
    'focus:outline-hidden',
  ],
  {
    variants: {
      variant: {
        default: ['data-[state=on]:bg-primary', 'active:bg-[#E57748]'],
        success: [
          'data-[state=on]:bg-[#22C55E]',
          'active:bg-[#16A34A]',
          'hover:after:data-[state=on]:ring-[#22C55E]/20',
        ],
        destructive: [
          'data-[state=on]:bg-destructive',
          'active:bg-[#DC284C]',
          'hover:after:data-[state=on]:ring-white/30',
        ],
      },
      disabled: {
        true: [
          'opacity-50',
          'hover:cursor-not-allowed',
          'hover:after:ring-0',
          'focus:after:ring-0',
          'active:after:ring-0',
        ],
      },
    },
    defaultVariants: {
      variant: 'default',
      disabled: false,
    },
  },
);

type ToggleCVAProps = {
  /**
   * Visual style variant of the toggle. Determines the active color scheme.
   * - `default` - Primary orange color when active
   * - `success` - Green color when active for positive actions
   * - `destructive` - Red color when active for dangerous actions
   * @default 'default'
   */
  variant?: VariantProps<typeof toggleVariants>['variant'];

  /**
   * Indicates if the toggle is in a disabled state. Prevents user interaction and applies visual disabled styling.
   * @default false
   */
  disabled?: VariantProps<typeof toggleVariants>['disabled'];
};

/**
 * Control binary settings and preferences with an intuitive switch that provides immediate visual feedback for on/off states.
 *
 * @param props - Toggle properties. See {@link ToggleCVAProps} for detailed prop documentation including inherited CVA variants.
 * @param ref - Forwarded ref to the toggle element
 * @returns Rendered toggle component
 *
 * @example
 * ```tsx
 * // Basic toggle
 * <Toggle />
 *
 * // Controlled toggle
 * <Toggle pressed={isEnabled} onPressedChange={setIsEnabled} />
 *
 * // Success variant toggle
 * <Toggle variant="success" />
 *
 * // Destructive variant toggle
 * <Toggle variant="destructive" />
 *
 * // Disabled toggle
 * <Toggle disabled />
 * ```
 */
const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> & ToggleCVAProps
>(({ className, variant, ...props }, ref) => (
  <TogglePrimitive.Root
    ref={ref}
    className={cn(
      toggleVariants({ variant, className, disabled: props.disabled }),
    )}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

export default Toggle;
